/**
 * Team Management Page
 * Enterprise feature for managing organization users
 */

import { BaseComponent } from '../lib/BaseComponent';
import { api } from '../lib/api';
import { router } from '../lib/router';
import { Toast } from './shared/Toast';
import { Modal } from './shared/Modal';
import { buildAppNav, APP_NAV_STYLES } from '../lib/app-nav.js';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'inspector';
  status: 'active' | 'inactive' | 'suspended';
  created_at: number;
  last_login_at?: number;
  phone_number?: string;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  created_at: number;
  expires_at: number;
  invited_by_name?: string;
}

export class TeamPage extends BaseComponent {
  private users: User[] = [];
  private invitations: Invitation[] = [];
  private loading = true;
  private currentUserRole: string = '';

  protected async render(): Promise<void> {
    // Add styles
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    // Create container
    const container = this.createElement('div', { className: 'team-page' });

    // Same navigation as every other signed-in page. This page previously had
    // none, so it could only be reached by typing the URL and offered no way
    // out except the browser's back button.
    const navHeader = this.createElement('header', { className: 'list-header' });
    const navContent = this.createElement('div', { className: 'header-content' });
    navContent.appendChild(this.createElement('h1', { className: 'logo' }, 'FireGuard'));
    navContent.appendChild(
      buildAppNav(
        (t, p, txt) => this.createElement(t as any, p as any, txt as any),
        '/team',
        () => {
          api.logout();
          router.navigate('/login');
        }
      )
    );
    navHeader.appendChild(navContent);
    container.appendChild(navHeader);

    // Header
    const header = this.createElement('div', { className: 'page-header' });
    const titleWrap = this.createElement('div', { className: 'page-title' });
    const back = this.createElement('a', { href: '/dashboard', className: 'back-link' }, '← Dashboard');
    back.addEventListener('click', (e) => {
      e.preventDefault();
      router.navigate('/dashboard');
    });
    titleWrap.appendChild(back);
    titleWrap.appendChild(this.createElement('h1', { textContent: 'Team Management' }));
    header.appendChild(titleWrap);

    // Check permissions and show invite button
    const userPerms = await this.getUserPermissions();
    if (userPerms.includes('users:invite')) {
      const inviteBtn = this.createElement('button', {
        textContent: '+ Invite User',
        className: 'btn-primary'
      }) as HTMLButtonElement;
      inviteBtn.addEventListener('click', () => this.showInviteModal());
      header.appendChild(inviteBtn);
    }

    container.appendChild(header);

    // Loading state
    if (this.loading) {
      const spinner = this.createElement('div', { className: 'loading-spinner' });
      spinner.textContent = 'Loading team...';
      container.appendChild(spinner);
      this.shadow.appendChild(container);

      // Load data
      await this.loadData();
      return;
    }

    // Tabs for Users vs Invitations
    const tabs = this.createElement('div', { className: 'tabs' });
    const usersTab = this.createElement('button', {
      textContent: `Active Users (${this.users.length})`,
      className: 'tab active'
    }) as HTMLButtonElement;
    const invitationsTab = this.createElement('button', {
      textContent: `Pending Invitations (${this.invitations.length})`,
      className: 'tab'
    }) as HTMLButtonElement;

    usersTab.addEventListener('click', () => {
      usersTab.classList.add('active');
      invitationsTab.classList.remove('active');
      usersList.style.display = 'block';
      invitationsList.style.display = 'none';
    });

    invitationsTab.addEventListener('click', () => {
      invitationsTab.classList.add('active');
      usersTab.classList.remove('active');
      usersList.style.display = 'none';
      invitationsList.style.display = 'block';
    });

    tabs.appendChild(usersTab);
    tabs.appendChild(invitationsTab);
    container.appendChild(tabs);

    // Users list
    const usersList = this.createUsersList();
    container.appendChild(usersList);

    // Invitations list
    const invitationsList = this.createInvitationsList();
    invitationsList.style.display = 'none';
    container.appendChild(invitationsList);

    this.shadow.appendChild(container);
  }

  private async getUserPermissions(): Promise<string[]> {
    try {
      // Role comes from the session token; fall back to /api/auth/me ({ user, organization }).
      let role = api.currentUser()?.role || '';
      if (!role) {
        const data = await api.me();
        role = data.user?.role || '';
      }
      this.currentUserRole = role;

      // Map roles to permissions (simplified - should match backend RBAC)
      const rolePermissions: Record<string, string[]> = {
        admin: ['users:view', 'users:invite', 'users:edit', 'users:delete'],
        manager: ['users:view'],
        inspector: []
      };

      return rolePermissions[this.currentUserRole] || [];
    } catch (error) {
      console.error('Failed to get permissions:', error);
      return [];
    }
  }

  private async loadData(): Promise<void> {
    try {
      this.loading = true;

      // Load users
      const usersResponse = await api.get('/api/users');
      this.users = usersResponse.users || [];

      // Load invitations (if permission)
      try {
        const invitationsResponse = await api.get('/api/users/invitations');
        this.invitations = invitationsResponse.invitations || [];
      } catch (error) {
        // User may not have permission
        this.invitations = [];
      }

      this.loading = false;
      this.shadow.replaceChildren();
      await this.render(); // Re-render with data
    } catch (error: any) {
      console.error('Load team data error:', error);
      Toast.error(error.message || 'Failed to load team data');
      this.loading = false;
      this.shadow.replaceChildren();
      await this.render();
    }
  }

  private createUsersList(): HTMLElement {
    const container = this.createElement('div', { className: 'users-list' });

    if (this.users.length === 0) {
      const empty = this.createElement('div', {
        className: 'empty-state',
        textContent: 'No team members yet. Invite your first user!'
      });
      container.appendChild(empty);
      return container;
    }

    // Users table
    const table = this.createElement('table', { className: 'users-table' });

    // Header
    const thead = this.createElement('thead');
    const headerRow = this.createElement('tr');
    ['Name', 'Email', 'Role', 'Status', 'Last Login', 'Actions'].forEach(text => {
      const th = this.createElement('th', { textContent: text });
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Body
    const tbody = this.createElement('tbody');
    this.users.forEach(user => {
      const row = this.createUserRow(user);
      tbody.appendChild(row);
    });
    table.appendChild(tbody);

    container.appendChild(table);
    return container;
  }

  private createUserRow(user: User): HTMLElement {
    const row = this.createElement('tr');

    // Name
    const nameCell = this.createElement('td');
    nameCell.textContent = user.name;
    row.appendChild(nameCell);

    // Email
    const emailCell = this.createElement('td');
    emailCell.textContent = user.email;
    row.appendChild(emailCell);

    // Role
    const roleCell = this.createElement('td');
    const roleBadge = this.createElement('span', {
      className: `role-badge role-${user.role}`,
      textContent: user.role.charAt(0).toUpperCase() + user.role.slice(1)
    });
    roleCell.appendChild(roleBadge);
    row.appendChild(roleCell);

    // Status
    const statusCell = this.createElement('td');
    const statusBadge = this.createElement('span', {
      className: `status-badge status-${user.status}`,
      textContent: user.status.charAt(0).toUpperCase() + user.status.slice(1)
    });
    statusCell.appendChild(statusBadge);
    row.appendChild(statusCell);

    // Last Login
    const lastLoginCell = this.createElement('td');
    lastLoginCell.textContent = user.last_login_at
      ? new Date(user.last_login_at).toLocaleDateString()
      : 'Never';
    row.appendChild(lastLoginCell);

    // Actions
    const actionsCell = this.createElement('td', { className: 'actions-cell' });

    // Edit button (if permission)
    const editBtn = this.createElement('button', {
      textContent: 'Edit',
      className: 'btn-secondary btn-small'
    }) as HTMLButtonElement;
    editBtn.addEventListener('click', () => this.showEditModal(user));
    actionsCell.appendChild(editBtn);

    // Delete button (if permission and not self)
    const deleteBtn = this.createElement('button', {
      textContent: 'Remove',
      className: 'btn-danger btn-small'
    }) as HTMLButtonElement;
    deleteBtn.addEventListener('click', () => this.confirmDeleteUser(user));
    actionsCell.appendChild(deleteBtn);

    row.appendChild(actionsCell);
    return row;
  }

  private createInvitationsList(): HTMLElement {
    const container = this.createElement('div', { className: 'invitations-list' });

    if (this.invitations.length === 0) {
      const empty = this.createElement('div', {
        className: 'empty-state',
        textContent: 'No pending invitations'
      });
      container.appendChild(empty);
      return container;
    }

    // Invitations table
    const table = this.createElement('table', { className: 'invitations-table' });

    // Header
    const thead = this.createElement('thead');
    const headerRow = this.createElement('tr');
    ['Email', 'Role', 'Invited By', 'Sent', 'Expires', 'Status'].forEach(text => {
      const th = this.createElement('th', { textContent: text });
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Body
    const tbody = this.createElement('tbody');
    this.invitations.forEach(invitation => {
      const row = this.createInvitationRow(invitation);
      tbody.appendChild(row);
    });
    table.appendChild(tbody);

    container.appendChild(table);
    return container;
  }

  private createInvitationRow(invitation: Invitation): HTMLElement {
    const row = this.createElement('tr');

    // Email
    const emailCell = this.createElement('td');
    emailCell.textContent = invitation.email;
    row.appendChild(emailCell);

    // Role
    const roleCell = this.createElement('td');
    const roleBadge = this.createElement('span', {
      className: `role-badge role-${invitation.role}`,
      textContent: invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)
    });
    roleCell.appendChild(roleBadge);
    row.appendChild(roleCell);

    // Invited By
    const invitedByCell = this.createElement('td');
    invitedByCell.textContent = invitation.invited_by_name || 'Unknown';
    row.appendChild(invitedByCell);

    // Sent Date
    const sentCell = this.createElement('td');
    sentCell.textContent = new Date(invitation.created_at).toLocaleDateString();
    row.appendChild(sentCell);

    // Expires Date
    const expiresCell = this.createElement('td');
    const isExpired = Date.now() > invitation.expires_at;
    expiresCell.textContent = new Date(invitation.expires_at).toLocaleDateString();
    if (isExpired) {
      expiresCell.style.color = 'var(--danger)';
    }
    row.appendChild(expiresCell);

    // Status
    const statusCell = this.createElement('td');
    const statusBadge = this.createElement('span', {
      className: `status-badge status-${invitation.status}`,
      textContent: invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)
    });
    statusCell.appendChild(statusBadge);
    row.appendChild(statusCell);

    return row;
  }

  private modal: Modal | null = null;

  private ensureModal(): Modal {
    if (!this.modal) {
      this.modal = document.createElement('app-modal') as Modal;
      document.body.appendChild(this.modal);
    }
    return this.modal;
  }

  private modalStyles(): HTMLStyleElement {
    const style = document.createElement('style');
    style.textContent = `
      .tm-form { display: grid; gap: 14px; }
      .tm-field { display: flex; flex-direction: column; gap: 6px; }
      .tm-field label { font-weight: 600; font-size: 14px; color: #111827; }
      .tm-field input, .tm-field select { padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 15px; font-family: inherit; width: 100%; box-sizing: border-box; }
      .tm-help { font-size: 12.5px; color: #6b7280; line-height: 1.4; }
      .tm-roles { display: grid; gap: 6px; font-size: 13px; color: #374151; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px 12px; }
    `;
    return style;
  }

  private roleSelect(selected: string): HTMLSelectElement {
    const select = document.createElement('select');
    select.name = 'role';
    select.required = true;
    for (const [value, label] of [
      ['inspector', 'Inspector - runs monthly checks on their phone'],
      ['manager', 'Manager - locations, extinguishers, reports'],
      ['admin', 'Admin - everything, including team and billing'],
    ]) {
      const o = document.createElement('option');
      o.value = value;
      o.textContent = label;
      if (value === selected) o.selected = true;
      select.appendChild(o);
    }
    return select;
  }

  private footerButtons(primaryLabel: string, onPrimary: (btn: HTMLButtonElement) => Promise<void>): HTMLElement {
    const footer = document.createElement('div');
    footer.style.display = 'flex';
    footer.style.gap = '12px';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'btn-secondary';
    cancel.textContent = 'Cancel';
    cancel.addEventListener('click', () => this.modal?.close());
    const primary = document.createElement('button');
    primary.type = 'button';
    primary.className = 'btn-primary';
    primary.textContent = primaryLabel;
    primary.addEventListener('click', async () => {
      primary.disabled = true;
      try {
        await onPrimary(primary);
      } finally {
        primary.disabled = false;
      }
    });
    footer.appendChild(cancel);
    footer.appendChild(primary);
    return footer;
  }

  private showInviteModal(): void {
    const form = document.createElement('form');
    form.className = 'tm-form';
    form.appendChild(this.modalStyles());

    const emailField = document.createElement('div');
    emailField.className = 'tm-field';
    const emailLabel = document.createElement('label');
    emailLabel.textContent = 'Work email';
    const email = document.createElement('input');
    email.type = 'email';
    email.name = 'email';
    email.required = true;
    email.placeholder = 'name@company.com';
    email.autocomplete = 'off';
    emailField.appendChild(emailLabel);
    emailField.appendChild(email);
    form.appendChild(emailField);

    const roleField = document.createElement('div');
    roleField.className = 'tm-field';
    const roleLabel = document.createElement('label');
    roleLabel.textContent = 'Role';
    const role = this.roleSelect('inspector');
    roleField.appendChild(roleLabel);
    roleField.appendChild(role);
    const help = document.createElement('div');
    help.className = 'tm-help';
    help.textContent = 'They get an email with a link to set their password. The invitation expires in 7 days. Every plan includes unlimited users.';
    roleField.appendChild(help);
    form.appendChild(roleField);

    const modal = this.ensureModal();
    const footer = this.footerButtons('Send invitation', async () => {
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      try {
        const res = await api.inviteUser({ email: email.value.trim().toLowerCase(), role: role.value });
        const sent = res?.invitation?.email_sent;
        Toast.success(sent === false ? `Invitation created for ${email.value.trim()}` : `Invitation sent to ${email.value.trim()}`);
        modal.close();
        await this.loadData();
      } catch (error: any) {
        Toast.error(error.message || 'Could not send invitation');
      }
    });
    modal.open({ title: 'Invite a team member', content: form, footer, size: 'small' });
    setTimeout(() => email.focus(), 50);
  }

  private showEditModal(user: User): void {
    const form = document.createElement('form');
    form.className = 'tm-form';
    form.appendChild(this.modalStyles());

    const who = document.createElement('div');
    who.className = 'tm-help';
    who.textContent = `${user.name} · ${user.email}`;
    form.appendChild(who);

    const nameField = document.createElement('div');
    nameField.className = 'tm-field';
    const nameLabel = document.createElement('label');
    nameLabel.textContent = 'Name';
    const name = document.createElement('input');
    name.type = 'text';
    name.name = 'name';
    name.required = true;
    name.value = user.name;
    nameField.appendChild(nameLabel);
    nameField.appendChild(name);
    form.appendChild(nameField);

    const roleField = document.createElement('div');
    roleField.className = 'tm-field';
    const roleLabel = document.createElement('label');
    roleLabel.textContent = 'Role';
    const role = this.roleSelect(user.role);
    roleField.appendChild(roleLabel);
    roleField.appendChild(role);
    form.appendChild(roleField);

    const statusField = document.createElement('div');
    statusField.className = 'tm-field';
    const statusLabel = document.createElement('label');
    statusLabel.textContent = 'Status';
    const status = document.createElement('select');
    status.name = 'status';
    for (const [value, label] of [['active', 'Active'], ['inactive', 'Inactive - cannot sign in']]) {
      const o = document.createElement('option');
      o.value = value;
      o.textContent = label;
      if (value === user.status) o.selected = true;
      status.appendChild(o);
    }
    statusField.appendChild(statusLabel);
    statusField.appendChild(status);
    form.appendChild(statusField);

    const modal = this.ensureModal();
    const footer = this.footerButtons('Save changes', async () => {
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      try {
        await api.updateUser(user.id, { name: name.value.trim(), role: role.value, status: status.value });
        Toast.success('Team member updated');
        modal.close();
        await this.loadData();
      } catch (error: any) {
        Toast.error(error.message || 'Could not update team member');
      }
    });
    modal.open({ title: 'Edit team member', content: form, footer, size: 'small' });
  }

  private async confirmDeleteUser(user: User): void {
    if (!confirm(`Are you sure you want to remove ${user.name} from the team?`)) {
      return;
    }

    try {
      await api.delete(`/api/users/${user.id}`);
      Toast.success('User removed successfully');
      await this.loadData(); // Reload data
    } catch (error: any) {
      console.error('Delete user error:', error);
      Toast.error(error.message || 'Failed to remove user');
    }
  }

  private getStyles(): string {
    return `
      ${APP_NAV_STYLES}
      .list-header { background: #fff; border-bottom: 1px solid var(--gray-200, #e5e7eb); margin-bottom: 16px; }
      .header-content { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; justify-content: space-between; max-width: 1200px; margin: 0 auto; padding: 12px 16px; }
      .logo { font-size: 20px; font-weight: 800; color: var(--primary, #b91c1c); margin: 0; }

      .team-page {
        padding: 20px;
        max-width: 1200px;
        margin: 0 auto;
      }

      .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 30px;
      }

      .page-title { display: flex; flex-direction: column; gap: 6px; }
      .back-link { color: var(--primary); text-decoration: none; font-size: 14px; }

      .page-header h1 {
        font-size: 24px;
        font-weight: 600;
        color: var(--gray-900);
        margin: 0;
      }

      .tabs {
        display: flex;
        gap: 0;
        margin-bottom: 20px;
        border-bottom: 2px solid var(--gray-200);
      }

      .tab {
        padding: 12px 24px;
        background: none;
        border: none;
        border-bottom: 2px solid transparent;
        font-size: 14px;
        font-weight: 500;
        color: var(--gray-600);
        cursor: pointer;
        transition: all 0.2s;
        margin-bottom: -2px;
      }

      .tab:hover {
        color: var(--gray-900);
      }

      .tab.active {
        color: var(--primary);
        border-bottom-color: var(--primary);
      }

      .users-table,
      .invitations-table {
        width: 100%;
        border-collapse: collapse;
        background: white;
        border-radius: var(--border-radius);
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .users-table th,
      .invitations-table th {
        background: var(--gray-50);
        padding: 12px 16px;
        text-align: left;
        font-size: 12px;
        font-weight: 600;
        color: var(--gray-700);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .users-table td,
      .invitations-table td {
        padding: 16px;
        border-top: 1px solid var(--gray-200);
        font-size: 14px;
        color: var(--gray-900);
      }

      .role-badge,
      .status-badge {
        display: inline-block;
        padding: 4px 10px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 500;
      }

      .role-badge.role-admin {
        background: var(--danger-light);
        color: var(--danger);
      }

      .role-badge.role-manager {
        background: var(--primary-light);
        color: var(--primary);
      }

      .role-badge.role-inspector {
        background: var(--gray-200);
        color: var(--gray-700);
      }

      .status-badge.status-active {
        background: var(--success-light);
        color: var(--success);
      }

      .status-badge.status-inactive,
      .status-badge.status-suspended {
        background: var(--gray-200);
        color: var(--gray-600);
      }

      .status-badge.status-pending {
        background: var(--warning-light);
        color: var(--warning);
      }

      .status-badge.status-expired {
        background: var(--danger-light);
        color: var(--danger);
      }

      .actions-cell {
        display: flex;
        gap: 8px;
      }

      .btn-small {
        padding: 6px 12px;
        font-size: 12px;
      }

      .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: var(--gray-500);
        font-size: 14px;
      }

      .loading-spinner {
        text-align: center;
        padding: 60px 20px;
        color: var(--gray-500);
      }

      @media (max-width: 768px) {
        .team-page {
          padding: 15px;
        }

        .page-header {
          flex-direction: column;
          align-items: flex-start;
          gap: 15px;
        }

        .users-table,
        .invitations-table {
          font-size: 12px;
        }

        .users-table th,
        .invitations-table th {
          padding: 8px;
        }

        .users-table td,
        .invitations-table td {
          padding: 12px 8px;
        }

        .actions-cell {
          flex-direction: column;
        }
      }
    `;
  }
}

customElements.define('team-page', TeamPage);
